import React, { useState, useEffect } from "react";
import Dropdown from "../../../components/ui/dropdown";
import Input from "../../../components/ui/input";
import Button from "../../../components/ui/button";
import styles from "./information.module.css";
import FormSection from "../../../components/screens/form-section";
import Checkbox from "../../../components/ui/checkbox";
import ContactItem from "../../../components/screens/contact-item";
import EditContactModal from "../../../components/modals/edit-contact-modal";
import UnsavedChangesModal from "../../../components/modals/unsaved-changes-modal";
import { showSuccessToast, showErrorToast, notifications } from "../../../utils/notifications";
import {
  getJobTypes,
  getCompanies,
  getStates,
  getActiveUsers,
  getLocalities,
  editRecord,
} from "../../../services/apiService";
import { useOutletContext, useParams } from "react-router-dom";
import { useTwilioCall } from "../../../hooks/useTwilioCall";
import usePageTitle from "../../../hooks/usePageTitle";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

const Information = () => {
  const { id: expedienteId } = useParams();
  usePageTitle(`Expediente ${expedienteId}`, 'Información');
  // Recibir isLockedByOther desde el contexto del padre para deshabilitar edición
  const { recordData, isLockedByOther, refetchRecord } = useOutletContext();
  console.log("Datos del expediente:", recordData);

  // Hook de Twilio para llamadas
  const { call: makeCall } = useTwilioCall();

  const [formData, setFormData] = useState({
    company: null,
    date: "",
    status: null,
    jobType: null,
    assignedUser: null, // Campo para el encargado
    companyNumber: "",
    clientName: "",
    clientAddress: "",
    clientLocality: null, // Cambiar a objeto para dropdown
    clientNIF: "",
    billingName: "",
    billingAddress: "",
    billingLocality: null, // Cambiar a objeto para dropdown
    billingNIF: "",
    service: "",
    // Campos para el contacto actual
    contactName: "",
    contactAddress: "",
    contactPhone: "",
    contactInfo: "",
    contactRol: null,
  });
  const [dropdownKey, setDropdownKey] = useState(0);

  // Lista de contactos creados
  const [contacts, setContacts] = useState([]);

  // Modal de editar contacto
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const [isBillingDifferent, setIsBillingDifferent] = useState(false);
  const [jobTypeOptions, setJobTypeOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]); // Opciones de usuarios activos
  const [localityOptions, setLocalityOptions] = useState([]); // Opciones de localidades

  // Estados para rastrear cambios sin guardar
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialContacts, setInitialContacts] = useState([]);
  const [initialIsBillingDifferent, setInitialIsBillingDifferent] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hook para advertir sobre cambios sin guardar
  const { showModal, handleCancel, handleConfirm, message } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    '¿Estás seguro de que quieres salir? Los cambios no guardados en el expediente se perderán.'
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    setFormData({ ...formData, [field]: option });
  };

  const handleBillingCheckboxChange = () => {
    setIsBillingDifferent(!isBillingDifferent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const expedienteId = recordData?.expedienteId;
    if (!expedienteId) {
      console.error("No hay ID de expediente para guardar");
      return;
    }

    try {
      // Preparar los datos según la estructura de la API
      const dataToSend = {
        usuarioId: formData.assignedUser?.value || null,
        companiaId: formData.company?.value || null,
        fecha: formData.date || null,
        estado: formData.status?.codigo || formData.status?.value || null,
        tipoTrabajoId: formData.jobType?.value || null,
        numeroCompania: formData.companyNumber || "",
        servicio: formData.service || "",
        nombreCliente: formData.clientName || "",
        domicilioCliente: formData.clientAddress || "",
        localidadClienteId: formData.clientLocality?.value || null,
        nifCliente: formData.clientNIF || "",
        difFacturacion: isBillingDifferent,
        nombreFacturacion: isBillingDifferent ? formData.billingName || "" : "",
        domicilioFacturacion: isBillingDifferent
          ? formData.billingAddress || ""
          : "",
        localidadFacturacionId: isBillingDifferent
          ? formData.billingLocality?.value || null
          : null,
        nifFacturacion: isBillingDifferent ? formData.billingNIF || "" : "",
        contactos: contacts.map((contact) => ({
          ...(contact.contactoId && { contactoId: contact.contactoId }),
          nombre: contact.name || contact.nombre || "",
          piso: contact.address || contact.piso || "",
          telefono: contact.phone || contact.telefono || "",
          info: contact.info || "",
          contactoRol: contact.rol?.value || contact.contactoRol || null,
        })),
      };

      console.log("Datos a enviar:", dataToSend);
      console.log("Contactos detalle:", contacts);
      console.log("Contactos mapeados:", dataToSend.contactos);

      const result = await editRecord(expedienteId, dataToSend);
      console.log("Expediente guardado exitosamente:", result);

      // Mostrar mensaje de éxito
      showSuccessToast(notifications.save.success);

      // Actualizar el estado inicial con los datos guardados
      setInitialFormData({...formData});
      setInitialContacts([...contacts]);
      setInitialIsBillingDifferent(isBillingDifferent);
      setHasUnsavedChanges(false);

      // Forzar re-fetch del expediente en el padre si está disponible
      await refetchRecord();
   
    } catch (error) {
      console.error("Error al guardar el expediente:", error);
      showErrorToast(notifications.save.error);
    }
  };

  // Función para agregar un nuevo contacto
  const addContact = (e) => {
    e.preventDefault();

    const newContact = {
      id: Date.now(), // ID único para cada contacto
      name: formData.contactName,
      address: formData.contactAddress,
      phone: formData.contactPhone,
      info: formData.contactInfo,
      rol: formData.contactRol,
    };

    setContacts([...contacts, newContact]);

    // Limpiar los campos de contacto después de agregarlo
    setFormData({
      ...formData,
      contactName: "",
      contactAddress: "",
      contactPhone: "",
      contactInfo: "",
      contactRol: null, // Asegurarse de que se resetea a null
    });
    setDropdownKey((prev) => prev + 1);
  };
  // Función para eliminar un contacto
  const removeContact = (contactId) => {
    setContacts(contacts.filter((contact) => contact.id !== contactId));
  };

  // Función para abrir modal de edición de contacto
  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setIsEditModalOpen(true);
  };

  // Función para llamar a un contacto
  const handleCallContact = (contact) => {
    if (contact.phone) {
      makeCall(contact.phone);
      showSuccessToast(`Llamando a ${contact.name}...`);
    } else {
      showErrorToast("El contacto no tiene número de teléfono");
    }
  };

  // Función para guardar cambios de contacto editado
  const handleSaveContact = async (updatedContact) => {
    try {
      console.log("handleSaveContact - updatedContact:", updatedContact);
      console.log("handleSaveContact - contacts antes:", contacts);
      
      // Actualizar el contacto en la lista local
      const updatedContacts = contacts.map(contact => 
        contact.id === updatedContact.id ? updatedContact : contact
      );
      
      console.log("handleSaveContact - updatedContacts:", updatedContacts);
      setContacts(updatedContacts);

      // Preparar datos del expediente completo con el contacto actualizado
      const expedienteData = {
        usuarioId: formData.assignedUser?.value || null,
        companiaId: formData.company?.value || null,
        fecha: formData.date || null,
        estado: formData.status?.codigo || formData.status?.value || null,
        tipoTrabajoId: formData.jobType?.value || null,
        numeroCompania: formData.companyNumber || "",
        servicio: formData.service || "",
        nombreCliente: formData.clientName || "",
        domicilioCliente: formData.clientAddress || "",
        localidadClienteId: formData.clientLocality?.value || null,
        nifCliente: formData.clientNIF || "",
        difFacturacion: isBillingDifferent,
        nombreFacturacion: isBillingDifferent ? formData.billingName || "" : "",
        domicilioFacturacion: isBillingDifferent ? formData.billingAddress || "" : "",
        localidadFacturacionId: isBillingDifferent ? formData.billingLocality?.value || null : null,
        nifFacturacion: isBillingDifferent ? formData.billingNIF || "" : "",
        contactos: updatedContacts.map(contact => ({
          ...(contact.contactoId && { contactoId: contact.contactoId }),
          nombre: contact.name || "",
          direccion: contact.address || "",
          telefono: contact.phone || "",
          info: contact.info || "",
          contactoRol: contact.rol?.value || null,
        })),
      };

      console.log("handleSaveContact - expedienteData a enviar:", expedienteData);
      console.log("handleSaveContact - contactos a enviar:", expedienteData.contactos);

      
      // Guardar en el servidor
      await editRecord(recordData.expedienteId, expedienteData);
      // Intentar refetch del expediente para sincronizar datos
      await refetchRecord();
  

      setIsEditModalOpen(false);
      setEditingContact(null);
      showSuccessToast("Para que se guarden lo cambios tienes que guardar el expediente");
    } catch (error) {
      console.error("Error al guardar contacto:", error);
      showErrorToast("Error al actualizar el contacto");
    }
  };

  // Función para rellenar los campos de contacto al hacer click en un contacto existente
  const handleContactClick = (contact) => {
    console.log("Contact clicked:", contact);
    console.log("Contact rol:", contact.rol);
    console.log("Available rolOptions:", rolOptions);
    
    // Encontrar el objeto rol correcto en rolOptions
    let selectedRol = null;
    if (contact.rol) {
      // Si contact.rol es un objeto con value, buscar por value
      if (typeof contact.rol === 'object' && contact.rol.value) {
        selectedRol = rolOptions.find(option => option.value === contact.rol.value);
        console.log("Found rol by object value:", selectedRol);
      } 
      // Si contact.rol es un string, buscar por value
      else if (typeof contact.rol === 'string') {
        selectedRol = rolOptions.find(option => option.value === contact.rol);
        console.log("Found rol by string value:", selectedRol);
      }
      // Si no encuentra nada, usar el objeto original
      else {
        selectedRol = contact.rol;
        console.log("Using original rol:", selectedRol);
      }
    }

    console.log("Final selectedRol:", selectedRol);

    setFormData({
      ...formData,
      contactName: contact.name,
      contactAddress: contact.address,
      contactPhone: contact.phone,
      contactInfo: contact.info || "",
      contactRol: selectedRol,
    });
    // Forzar re-render del dropdown para que muestre el valor seleccionado
    setDropdownKey((prev) => prev + 1);
  };

  // Verificar si los campos obligatorios del contacto están completos
  const isContactFormValid = () => {
    return formData.contactName.trim();
  };

  const rolOptions = React.useMemo(() => [
    { value: "PRESIDENTE", label: "Presidente" },
    { value: "AFECTADO", label: "Afectado" },
    { value: "CONSERJE", label: "Conserje" },
    { value: "CAUSANTE", label: "Causante" },
    { value: "ARQUITECTO", label: "Arquitecto" },
  ], []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobTypes, companies, states, activeUsers, localities] = await Promise.all([
          getJobTypes(),
          getCompanies(),
          getStates(),
          getActiveUsers(),
          getLocalities(),
        ]);

        // Mapear opciones
        const jobTypeOptions = jobTypes.map((item) => ({
          value: item.id,
          label: item.nombre,
        }));

        const companyOptions = companies.map((item) => ({
          value: item.id,
          label: item.nombre,
        }));

        const statusOptions = states.map((item) => ({
          value: item.id,
          label: item.nombre,
          codigo: item.codigo,
        }));

        console.log("States raw data:", states);
        console.log("Status Options:", statusOptions);

        // Mapear opciones de localidad
        const localityOptions = localities.map((item) => ({
          value: item.id,
          label: `${item.codigoPostal} - ${item.municipio}`,
        }));

        // Construir lista de IDs del historial para fácil búsqueda
        const historyIds = recordData?.usuariosHistoryIds || [];
        
        const userOptionsFormatted = activeUsers.map((user) => ({
          value: user.usuarioId, // Usar usuarioId en lugar de id
          label: `${user.nombre}`,
          isInHistory: historyIds.includes(user.usuarioId), // Marcar si está en el historial
          style: historyIds.includes(user.usuarioId) ? { 
            backgroundColor: '#fff3cd', 
            color: '#856404',
            fontWeight: 'bold'
          } : undefined
        }));

        console.log("Active Users raw:", activeUsers); 
        console.log("User Options Formatted:", userOptionsFormatted);

        // Actualizar estados de opciones
        setJobTypeOptions(jobTypeOptions);
        setCompanyOptions(companyOptions);
        setStatusOptions(statusOptions);
        setUserOptions(userOptionsFormatted);
        setLocalityOptions(localityOptions);

        console.log("Job Types Options:", jobTypeOptions);
        console.log("Company Options:", companyOptions);
        console.log("Status Options:", statusOptions);
        console.log("Record Data:", recordData);
        console.log("User Options:", activeUsers);
        // Inicializar datos del formulario si existe recordData
        if (recordData) {
          const findOption = (options, id) =>
            options.find((opt) => opt.value === id) || null;

          const findOptionByCode = (options, code) =>
            options.find(
              (opt) =>
                opt.codigo === code || opt.label === code || opt.value === code
            ) || null;

          console.log("Mapping data...");
          console.log("Company ID:", recordData.companiaId);
          console.log("Estado recibido:", recordData.estado);
          console.log("Tipo Trabajo ID:", recordData.tipoTrabajoId);

          // Buscar estado por código
          const foundStatus = findOptionByCode(
            statusOptions,
            recordData.estado
          );
          console.log("Estado encontrado:", foundStatus);

          const mappedFormData = {
            company: findOption(companyOptions, recordData.companiaId),
            date: recordData.fecha || "",
            status: foundStatus,
            jobType: findOption(jobTypeOptions, recordData.tipoTrabajoId),
            assignedUser: findOption(
              userOptionsFormatted,
              recordData.usuarioId
            ), // Mapear el encargado
            companyNumber: recordData.numeroCompania || "",
            clientName: recordData.nombreCliente || "",
            clientAddress: recordData.domicilioCliente || "",
            clientLocality: localityOptions.find(locality => 
              locality.value === recordData.localidadClienteId
            ) || null,
            clientNIF: recordData.nifCliente || "",
            billingName: recordData.nombreFacturacion || "",
            billingAddress: recordData.domicilioFacturacion || "",
            billingLocality: localityOptions.find(locality => 
              locality.value === recordData.localidadFacturacionId
            ) || null,
            billingNIF: recordData.nifFacturacion || "",
            service: recordData.servicio || "",
            contactName: "",
            contactAddress: "",
            contactPhone: "",
            contactInfo: "",
            contactRol: null,
          };

          console.log("Mapped Form Data:", mappedFormData);
          setFormData(mappedFormData);
          // Guardar estado inicial para comparación
          setInitialFormData(mappedFormData);

          // Mapear contactos si existen
          if (recordData.contactos && Array.isArray(recordData.contactos)) {
            const mappedContacts = recordData.contactos.map((contact) => {
              // Buscar el rol en rolOptions si contactoRol es un string
              let rolObject = null;
              if (contact.contactoRol) {
                rolObject = rolOptions.find(opt => opt.value === contact.contactoRol) || null;
              }

              return {
                id: contact.contactoId || Date.now() + Math.random(),
                contactoId: contact.contactoId, // Mantener el ID original de la API
                name: contact.nombre || "",
                address: contact.direccion || contact.piso || "",
                phone: contact.telefono || "",
                info: contact.info || "",
                rol: rolObject,
              };
            });
            
            console.log("Mapped Contacts:", mappedContacts);
            setContacts(mappedContacts);
            // Guardar contactos iniciales
            setInitialContacts(mappedContacts);
          }

          setIsBillingDifferent(recordData.difFacturacion || false);
          setInitialIsBillingDifferent(recordData.difFacturacion || false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [recordData, rolOptions]);

  // useEffect para detectar cambios sin guardar
  useEffect(() => {
    if (!initialFormData) {
      // Si no hay datos iniciales aún, no hay cambios
      setHasUnsavedChanges(false);
      return;
    }

    // Comparar formData con initialFormData
    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    const contactsChanged = JSON.stringify(contacts) !== JSON.stringify(initialContacts);
    const billingChanged = isBillingDifferent !== initialIsBillingDifferent;

    setHasUnsavedChanges(formDataChanged || contactsChanged || billingChanged);
  }, [formData, contacts, isBillingDifferent, initialFormData, initialContacts, initialIsBillingDifferent]);

  return (
    <div style={{ paddingTop: "24px" }}>

      {/* Formulario */}
      <form onSubmit={handleSubmit}>
        <FormSection title="General" style={{ marginBottom: "24px" }} renderRight={() => <Button
          type="submit"
          variant="primary"
          disabled={!!isLockedByOther}
          style={{
            cursor: isLockedByOther ? "not-allowed" : "pointer",
          }}
        >
          GUARDAR EXPEDIENTE
        </Button>}>
          <div className={styles.sectionRow}>
            <Dropdown
              label={"Compañía"}
              options={companyOptions}
              onSelect={(option) =>
                !isLockedByOther && handleDropdownSelect("company", option)
              }
              selected={formData.company}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />

            <Input
              label="Fecha"
              type="date"
              name="date"
              value={formData.date}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Dropdown
              label={"Estado"}
              options={statusOptions}
              onSelect={(option) =>
                !isLockedByOther && handleDropdownSelect("status", option)
              }
              selected={formData.status}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Dropdown
              label={"Tipo de Trabajo"}
              options={jobTypeOptions}
              onSelect={(option) =>
                !isLockedByOther && handleDropdownSelect("jobType", option)
              }
              selected={formData.jobType}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Dropdown
              label={"Encargado"}
              options={userOptions}
              onSelect={(option) =>
                !isLockedByOther && handleDropdownSelect("assignedUser", option)
              }
              selected={formData.assignedUser}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Input
              label="Número de Compañía"
              name="companyNumber"
              placeholder={"Numero de Compañía"}
              value={formData.companyNumber}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
          </div>
        </FormSection>

        <FormSection style={{ marginBottom: "24px" }} title="Servicios">
          <textarea
            name="service"
            placeholder={"Servicios necesarios"}
            value={formData.service}
            onChange={!isLockedByOther ? handleInputChange : undefined}
            className={styles.workTextarea}
            style={{
              width: "100%",
              height: "96px",
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "12px",
              paddingRight: "12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontFamily: "var(--font-roboto)",
              fontSize: "14px",
              color: "black",
              resize: "none",
              outline: "none",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflow: "auto",
            }}
            disabled={!!isLockedByOther}
            spellCheck="true"
          />
        </FormSection>

        <FormSection
          title="Cliente"
          style={{ marginBottom: "24px" }}
          renderRight={() => (
            <Checkbox
              label="Facturación diferente"
              checked={isBillingDifferent}
              onChange={
                !isLockedByOther ? handleBillingCheckboxChange : undefined
              }
              disabled={!!isLockedByOther}
            />
          )}
        >
          <div className={styles.sectionRow}>
            <Input
              label="Nombre"
              name="clientName"
              placeholder={"Nombre del cliente"}
              value={formData.clientName}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Input
              label="Domicilio"
              name="clientAddress"
              placeholder={"Domicilio del cliente"}
              value={formData.clientAddress}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Dropdown
              label="Localidad"
              options={localityOptions}
              onSelect={(option) => handleDropdownSelect("clientLocality", option)}
              selected={formData.clientLocality}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Input
              label="NIF"
              name="clientNIF"
              placeholder={"NIF del cliente"}
              value={formData.clientNIF}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
          </div>
        </FormSection>

        {isBillingDifferent && (
          <FormSection
            title="Facturación"
            style={{ marginBottom: "24px" }}
          >
            <div className={styles.sectionRow}>
              <Input
                label="Nombre"
                name="billingName"
                placeholder={"Nombre de facturación"}
                value={formData.billingName}
                onChange={!isLockedByOther ? handleInputChange : undefined}
                style={{ flex: 1 }}
                disabled={!!isLockedByOther}
              />
              <Input
                label="Domicilio"
                name="billingAddress"
                placeholder={"Domicilio de facturación"}
                value={formData.billingAddress}
                onChange={!isLockedByOther ? handleInputChange : undefined}
                style={{ flex: 1 }}
                disabled={!!isLockedByOther}
              />
              <Dropdown
                label="Localidad"
                options={localityOptions}
                onSelect={(option) => handleDropdownSelect("billingLocality", option)}
                selected={formData.billingLocality}
                style={{ marginTop: "3px", width: "100%" }}
                mainStyle={{ flex: 1 }}
                disabled={!!isLockedByOther}
              />
              <Input
                label="NIF"
                name="billingNIF"
                placeholder={"NIF de facturación"}
                value={formData.billingNIF}
                onChange={!isLockedByOther ? handleInputChange : undefined}
                style={{ flex: 1 }}
                disabled={!!isLockedByOther}
              />
            </div>
          </FormSection>
        )}

        <FormSection title="Contactos" style={{ marginBottom: "24px" }}>
          <div className={styles.sectionRow}>
            <Input
              label="Nombre"
              name="contactName"
              placeholder={"Nombre del contacto"}
              value={formData.contactName}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Input
              label="Domicilio (Piso, Puerta, etc.)"
              name="contactAddress"
              placeholder={"Domicilio del contacto"}
              value={formData.contactAddress}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Input
              label="Telefono"
              name="contactPhone"
              placeholder={"Telefono del contacto"}
              value={formData.contactPhone}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Input
              label="Información adicional"
              name="contactInfo"
              placeholder={"Información adicional"}
              value={formData.contactInfo}
              onChange={!isLockedByOther ? handleInputChange : undefined}
              style={{ flex: 1 }}
              disabled={!!isLockedByOther}
            />
            <Dropdown
              key={dropdownKey}
              label={"Rol"}
              options={rolOptions}
              onSelect={(option) =>
                !isLockedByOther && handleDropdownSelect("contactRol", option)
              }
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
              selected={formData.contactRol}
              disabled={!!isLockedByOther}
            />
          </div>
          {/* Lista de contactos */}
          {contacts.length > 0 && (
            <div className={styles.contactsList}>
              {contacts.map((contact) => (
                <ContactItem
                  key={contact.id}
                  contact={contact}
                  onRemove={!isLockedByOther ? removeContact : undefined}
                  onClick={!isLockedByOther ? handleContactClick : undefined}
                  onEdit={!isLockedByOther ? handleEditContact : undefined}
                  onCall={handleCallContact}
                  disabled={!!isLockedByOther}
                />
              ))}
            </div>
          )}

          <Button
            type="button"
            onClick={addContact}
            disabled={!isContactFormValid() || !!isLockedByOther}
            style={{
              opacity: isContactFormValid() && !isLockedByOther ? 1 : 0.5,
              cursor:
                isContactFormValid() && !isLockedByOther
                  ? "pointer"
                  : "not-allowed",
              marginTop: "24px",
              width: "fit-content",
            }}
          >
            AÑADIR CONTACTO
          </Button>
        </FormSection>
      </form>

      {/* Modal de editar contacto */}
      <EditContactModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact}
        onSave={handleSaveContact}
        rolOptions={rolOptions}
      />

      {/* Modal de advertencia de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        message={message}
      />
    </div>
  );
};

export default Information;
